import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const PageContainer = styled.div`
  padding: 120px 0 60px;
  background-color: var(--bg-light);
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-dark);
  margin-bottom: 2rem;
  text-align: center;
`;

const ContentSection = styled.section`
  background: var(--white);
  padding: 2.5rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
  margin-top: 2.5rem;
  
  &:first-child {
    margin-top: 0;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  color: var(--text-medium);
  line-height: 1.8;
  margin-bottom: 1.5rem;
`;

const List = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
`;

const ListItem = styled.li`
  font-size: 1rem;
  color: var(--text-medium);
  line-height: 1.8;
  margin-bottom: 0.75rem;
`;

const BackButton = styled(Link)`
  display: inline-block;
  background: linear-gradient(to right, var(--primary-color), var(--secondary-color));
  color: var(--white);
  font-weight: 600;
  padding: 0.875rem 1.75rem;
  border-radius: var(--border-radius);
  text-decoration: none;
  margin-bottom: 2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(var(--primary-rgb), 0.3);
  }
`;

const PolicyLink = styled(Link)`
  color: var(--primary-color);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LastUpdated = styled.p`
  font-size: 0.9rem;
  color: var(--text-light);
  text-align: center;
  margin-top: 3rem;
  font-style: italic;
`;

const PrivacyPolicy = () => {
  return (
    <PageContainer>
      <Container>
        <BackButton to="/">← Back to Home</BackButton>
        <PageTitle>Privacy Policy</PageTitle>
        
        <ContentSection>
          <Paragraph>
            At tinysmart.AI, we are committed to protecting your privacy and ensuring the security of your personal information. 
            This Privacy Policy outlines how we collect, use, disclose, and safeguard your information when you use our platform. 
            By using tinysmart.AI, you agree to the practices described in this policy.
          </Paragraph>
          
          <SectionTitle>Information We Collect</SectionTitle>
          <Paragraph>We may collect the following types of information:</Paragraph>
          <List>
            <ListItem>
              <strong>Personal Information:</strong> Name, email address, and contact details when you register for our service or contact us.
            </ListItem>
            <ListItem>
              <strong>Usage Data:</strong> Information about how you use our platform, including interactions with AI agents and features you access.
            </ListItem>
            <ListItem>
              <strong>Device Information:</strong> Information about your device, operating system, browser type, and IP address.
            </ListItem>
            <ListItem>
              <strong>Message Content:</strong> Content of messages you exchange with AI agents on our platform.
            </ListItem>
          </List>
          
          <SectionTitle>How We Use Your Information</SectionTitle>
          <Paragraph>We use the collected information for various purposes, including:</Paragraph>
          <List>
            <ListItem>To provide, maintain, and improve our services</ListItem>
            <ListItem>To process and respond to your inquiries</ListItem>
            <ListItem>To send you updates, notifications, and promotional content (which you can opt out of)</ListItem>
            <ListItem>To analyze usage patterns and optimize user experience</ListItem>
            <ListItem>To personalize your experience with our AI agents</ListItem>
            <ListItem>To detect, prevent, and address technical issues</ListItem>
          </List>
          
          <SectionTitle>Local Processing and Data Storage</SectionTitle>
          <Paragraph>
            tinysmart.AI is designed with privacy in mind. Whenever possible, we process data locally on your device to minimize data transmission. 
            Our platform offers on-device AI models that enable local processing of many interactions, providing enhanced privacy compared to 
            cloud-only AI solutions.
          </Paragraph>
          
          <SectionTitle>Data Security</SectionTitle>
          <Paragraph>
            We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, 
            or destruction. These measures include end-to-end encryption for message content and secure data storage practices. However, no method 
            of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
          </Paragraph>
          
          <SectionTitle>Data Sharing and Disclosure</SectionTitle>
          <Paragraph>We may share your information with:</Paragraph>
          <List>
            <ListItem>
              <strong>Service Providers:</strong> Third-party vendors who help us provide and improve our services
            </ListItem>
            <ListItem>
              <strong>Legal Requirements:</strong> When required by law, such as to comply with a subpoena or similar legal process
            </ListItem>
            <ListItem>
              <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, where your data may be transferred as part of the business assets
            </ListItem>
          </List>
          <Paragraph>
            We do not sell your personal information to third parties.
          </Paragraph>
          
          <SectionTitle>Your Rights</SectionTitle>
          <Paragraph>Depending on your location, you may have rights regarding your personal information, including:</Paragraph>
          <List>
            <ListItem>Access to your personal information</ListItem>
            <ListItem>Correction of inaccurate or incomplete data</ListItem>
            <ListItem>Deletion of your personal information</ListItem>
            <ListItem>Restriction or objection to processing of your data</ListItem>
            <ListItem>Data portability</ListItem>
          </List>
          <Paragraph>
            To exercise these rights, please contact us at contact@tinysmart.ai.
          </Paragraph>
          
          <SectionTitle>Children's Privacy</SectionTitle>
          <Paragraph>
            Our service is not intended for use by individuals under the age of 13. We do not knowingly collect personal information from children 
            under 13. If we discover that a child under 13 has provided us with personal information, we will promptly delete it.
          </Paragraph>
          
          <SectionTitle>Changes to This Privacy Policy</SectionTitle>
          <Paragraph>
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page 
            and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
          </Paragraph>
          
          <SectionTitle>Contact Us</SectionTitle>
          <Paragraph>
            If you have any questions about this Privacy Policy, please contact us at contact@tinysmart.ai.
          </Paragraph>
          
          <LastUpdated>Last Updated: April 21, 2025</LastUpdated>
        </ContentSection>
      </Container>
    </PageContainer>
  );
};

export default PrivacyPolicy; 